import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../../LocationPickerModal/styles.css';
import { X, Search, GeoAltFill, Check, Crosshair } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const customIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCAzMiA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE2IDQ4QzE2IDQ4IDMyIDI5LjMzMzMgMzIgMTZDMzIgNy4xNjM0NCAyNC44MzY2IDAgMTYgMEM3LjE2MzQ0IDAgMCA3LjE2MzQ0IDAgMTZDMCAyOS4zMzMzIDE2IDQ4IDE2IDQ4WiIgZmlsbD0iI0ZGRDcwMCIvPgo8Y2lyY2xlIGN4PSIxNiIgY3k9IjE2IiByPSI4IiBmaWxsPSIjMDAwMDAwIi8+Cjwvc3ZnPgo=',
  iconSize: [32, 48],
  iconAnchor: [16, 48],
  popupAnchor: [0, -48],
});

interface ContactAddressMapModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLocationSelect: (location: {
    name: string;
    latitude: number;
    longitude: number;
  }) => void;
  initialLocation?: {
    latitude: number;
    longitude: number;
  };
  title?: string;
  initialAddress?: string;
  disableAutoSelectOnMount?: boolean;
}

interface MapControllerProps {
  center: [number, number];
  onMapClick: (lat: number, lng: number) => void;
}

const MapController: React.FC<MapControllerProps> = ({ center, onMapClick }) => {
  const map = useMap();

  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);

  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });

  return null;
};

interface MapInitializerProps {
  mapRef: React.MutableRefObject<any>;
}

const MapInitializer: React.FC<MapInitializerProps> = ({ mapRef }) => {
  const map = useMap();

  useEffect(() => {
    mapRef.current = map;
    // Force map to recalculate size multiple times to ensure proper rendering
    const timeouts = [100, 200, 300];
    const timeoutIds = timeouts.map(delay => {
      return setTimeout(() => {
        if (map && map.getContainer && map.getContainer()) {
          try {
            map.invalidateSize();
          } catch (error) {
            console.warn("Leaflet invalidateSize error suppressed:", error);
          }
        }
      }, delay);
    });

    return () => {
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, [map, mapRef]);

  return null;
};

interface LocationSuggestion {
  name: string;
  latitude: number;
  longitude: number;
  display_name: string;
}

const ContactAddressMapModal: React.FC<ContactAddressMapModalProps> = ({
  isOpen,
  onClose,
  onLocationSelect,
  initialLocation = { latitude: 25.1413124, longitude: 55.0962782 },
  initialAddress = '',
  title = 'Select Location',
  disableAutoSelectOnMount = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [mapCenter, setMapCenter] = useState<[number, number]>([
    initialLocation.latitude,
    initialLocation.longitude,
  ]);
  const [markerPosition, setMarkerPosition] = useState<[number, number]>([
    initialLocation.latitude,
    initialLocation.longitude,
  ]);
  const [selectedLocationName, setSelectedLocationName] = useState('');
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mapRef = useRef<any>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const LOCATIONIQ_TOKEN = process.env.REACT_APP_LOCATIONIQ_TOKEN;

  useEffect(() => {
    if (isOpen) {
      setIsMapReady(false);
      setMapCenter([initialLocation.latitude, initialLocation.longitude]);
      setMarkerPosition([initialLocation.latitude, initialLocation.longitude]);

      if (initialAddress) {
        setSelectedLocationName(initialAddress);
        setSearchQuery(initialAddress);
      } else {
        setSelectedLocationName('');
        setSearchQuery('');
      }

      const openTimeout = setTimeout(() => {
        if (mapRef.current) {
          try {
            if (mapRef.current.getContainer && mapRef.current.getContainer()) {
              mapRef.current.invalidateSize();
            }
          } catch (e) {
            console.warn("Leaflet map invalidateSize error suppressed handling fast-close.", e);
          }
          setIsMapReady(true);
        }
        // Focus the search input when modal is ready
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);

      return () => {
        clearTimeout(openTimeout);
      };
    }
  }, [isOpen, initialLocation, initialAddress, disableAutoSelectOnMount]);

  const reverseGeocode = async (lat: number, lon: number) => {
    try {
      const response = await fetch(
        `https://us1.locationiq.com/v1/reverse?key=${LOCATIONIQ_TOKEN}&lat=${lat}&lon=${lon}&format=json`
      );
      const data = await response.json();
      const locationName = data?.display_name || 'Unknown Location';
      setSelectedLocationName(locationName);
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      setSelectedLocationName('Unknown Location');
    }
  };

  const handleMapClick = (lat: number, lng: number) => {
    setMarkerPosition([lat, lng]);
    reverseGeocode(lat, lng);
    setSearchQuery('');

    // Dismiss keyboard on mobile
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.length > 2) {
      searchTimeoutRef.current = setTimeout(() => {
        searchLocation(value);
      }, 500);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const searchLocation = async (query: string) => {
    try {
      const response = await fetch(
        `https://us1.locationiq.com/v1/search?key=${LOCATIONIQ_TOKEN}&q=${encodeURIComponent(query)}&format=json&limit=5&countrycodes=ae`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const mappedSuggestions: LocationSuggestion[] = data.map((item: any) => ({
          name: item.display_name,
          display_name: item.display_name,
          latitude: parseFloat(item.lat),
          longitude: parseFloat(item.lon),
        }));
        setSuggestions(mappedSuggestions);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error searching location:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: LocationSuggestion) => {
    setMapCenter([suggestion.latitude, suggestion.longitude]);
    setMarkerPosition([suggestion.latitude, suggestion.longitude]);
    setSelectedLocationName(suggestion.name);
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);

    // Dismiss keyboard on mobile
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setIsLoadingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setMapCenter([latitude, longitude]);
        setMarkerPosition([latitude, longitude]);
        await reverseGeocode(latitude, longitude);
        setSearchQuery('');
        setIsLoadingLocation(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('Unable to retrieve your location');
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleConfirm = () => {
    if (!selectedLocationName) {
      toast.warn('Please select a location');
      return;
    }
    onLocationSelect({
      name: selectedLocationName,
      latitude: markerPosition[0],
      longitude: markerPosition[1],
    });
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="location-picker-overlay">
      <div className="location-picker-modal">
        {/* Header */}
        <div className="modal-head">
          <h2 className="modal-title">{title}</h2>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div className="left-panel">
            <div style={{ position: 'relative', zIndex: 100 }}>
              <div className="location-search">
                <input
                  ref={searchInputRef}
                  type="text"
                  className="location-search-input"
                  placeholder="Search location, hotel, airport..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                />
                <Search className="search-icon" size={18} />
              </div>
              
              {/* Search Suggestions Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="search-suggestions">
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className="suggestion-item"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      <GeoAltFill size={16} className="suggestion-icon" />
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                        <span className="suggestion-text" style={{ fontWeight: 600 }}>{suggestion.name}</span>
                        <span className="suggestion-text" style={{ fontSize: '12px', opacity: 0.7 }}>{suggestion.display_name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="panel-box">
              <div className="panel-title">QUICK ACTIONS</div>
              <button
                className="current-location-pill"
                onClick={handleCurrentLocation}
                disabled={isLoadingLocation}
                title="Use current location"
              >
                {isLoadingLocation ? (
                  <div className="spinner-small" />
                ) : (
                  <Crosshair style={{ flexShrink: 0 }} size={16} />
                )}
                <span>Use Current Location</span>
              </button>
              
              <button
                className="small-pill"
                onClick={() => {
                  setMapCenter([25.0792, 55.1382]);
                  setMarkerPosition([25.0792, 55.1382]);
                  setSelectedLocationName('Dubai Marina, Dubai');
                }}
              >
                <GeoAltFill style={{ flexShrink: 0 }} size={16} />
                <span>Popular: Dubai Marina</span>
              </button>

              <button
                className="small-pill"
                onClick={() => {
                  setMapCenter([25.2532, 55.3657]);
                  setMarkerPosition([25.2532, 55.3657]);
                  setSelectedLocationName('DXB Terminal 3, Dubai Airport');
                }}
              >
                <GeoAltFill style={{ flexShrink: 0 }} size={16} />
                <span>Airport: DXB Terminal 3</span>
              </button>
            </div>
          </div>

          <div className="map-panel">
            <div className="map-wrapper map">
              <div className="map-note">Tap on map or use search. Reverse geocoded address should appear below.</div>
              {!isMapReady && (
                <div className="map-loading-overlay">
                  <div className="map-loading-spinner" />
                  <p className="map-loading-text">Loading map...</p>
                </div>
              )}
              <MapContainer
                key={`${mapCenter[0]}-${mapCenter[1]}-${isOpen}`}
                center={mapCenter}
                zoom={12}
                style={{ height: '100%', width: '100%' }}
                zoomControl={true}
                attributionControl={false}
              >
                <TileLayer
                  attribution=''
                  url={`https://tiles.locationiq.com/v3/streets/r/{z}/{x}/{y}.png?key=${LOCATIONIQ_TOKEN}`}
                />
                <Marker position={markerPosition} icon={customIcon} />
                <MapController center={mapCenter} onMapClick={handleMapClick} />
                <MapInitializer mapRef={mapRef} />
              </MapContainer>
            </div>

            <div className="selected">
              <div className="selected-info">
                <div className="selected-label">Selected Location</div>
                <div className="selected-main" title={selectedLocationName}>{selectedLocationName || 'Tap on map to select'}</div>
              </div>

              <div className="action-row">
                <button className="modal-btn" onClick={onClose}>
                  Cancel
                </button>
                <button
                  className="modal-btn primary"
                  onClick={handleConfirm}
                >
                  <Check size={18} style={{ flexShrink: 0 }} />
                  <span>Confirm Location</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ContactAddressMapModal;
